import { useState, type JSX } from 'react';
import {
  CAPACITY_PLANS,
  billingOverviewSchema,
  generateUuidV7,
  planCapacitySchema,
  planCodeSchema,
  formatExactMoney,
  type BotScope,
  type PlanCode,
} from '@cryptology-assist/domain';
import { botPath, idSchema, request, savedSchema, type Session } from './api';
import { useRemote } from './hooks';
import { ErrorNotice, Field, onSubmit, useMutation } from './components';
import {
  dateLabel,
  numberLabel,
  statusLabel,
  type Copy,
  type Language,
} from './copy';

export function Billing({
  scope,
  session,
  copy,
  language,
  canWrite,
  apiBase = botPath(scope, ''),
}: {
  scope: BotScope;
  session: Session;
  copy: Copy;
  language: Language;
  canWrite: boolean;
  apiBase?: string;
}): JSX.Element {
  const overview = useRemote(`${apiBase}billing`, billingOverviewSchema),
    capacity = useRemote(`${apiBase}capacity`, planCapacitySchema),
    mutation = useMutation();
  const [plan, setPlan] = useState<PlanCode | null>(null),
    [startsAt, setStartsAt] = useState(''),
    [confirmed, setConfirmed] = useState(false);
  const [larger, setLarger] = useState<PlanCode | null>(null),
    [largerConfirmed, setLargerConfirmed] = useState(false);
  const locale = language === 'es' ? 'es-PE' : 'en-US';
  // Only a plan with more conversations than the current one can start early.
  const largerPlans = capacity.data
    ? Object.values(CAPACITY_PLANS).filter(
        (item) => item.includedConversations > capacity.data!.included
      )
    : null;
  return (
    <section className="form-section">
      <h2>{copy.billing}</h2>
      <ErrorNotice
        error={overview.error ?? capacity.error ?? mutation.error}
        copy={copy}
      />
      {!overview.data && !overview.error && <p>{copy.loading}</p>}
      {overview.data && !overview.data.policy && (
        <p>{copy.billingNotConfigured}</p>
      )}
      {overview.data?.policy && (
        <>
          <p>
            {copy.billingAuthority}:{' '}
            {overview.data.policy.mode === 'crm'
              ? copy.billingCrm
              : copy.billingOperator}
          </p>
          <p>
            {overview.data.policy.autoRenew
              ? copy.billingAutoRenew
              : copy.billingManualRenew}
          </p>
          {canWrite && (
            <>
              <button
                className="quiet"
                disabled={mutation.busy}
                onClick={() =>
                  void mutation.run(async () => {
                    await request(`${apiBase}billing/sync`, savedSchema, {
                      csrf: session.csrf,
                      body: {},
                    });
                    overview.reload();
                  })
                }
              >
                {copy.billingSync}
              </button>
              <form
                className="billing-renewal"
                onSubmit={(event) =>
                  onSubmit(event, () =>
                    mutation.run(async () => {
                      if (!plan || !confirmed) return;
                      await request(`${apiBase}billing/renew`, idSchema, {
                        csrf: session.csrf,
                        body: {
                          id: generateUuidV7(),
                          plan,
                          startsAt: new Date(startsAt).toISOString(),
                        },
                      });
                      overview.reload();
                      setConfirmed(false);
                    })
                  )
                }
              >
                <Field label={copy.billingPlan}>
                  <select
                    required
                    value={plan ?? ''}
                    onChange={(event) => {
                      setPlan(planCodeSchema.parse(event.target.value));
                      setConfirmed(false);
                    }}
                  >
                    <option disabled value="">
                      {copy.choose}
                    </option>
                    {planCodeSchema.options.map((code) => (
                      <option key={code} value={code}>
                        {code}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label={copy.billingStarts}>
                  <input
                    required
                    type="datetime-local"
                    value={startsAt}
                    onChange={(event) => {
                      setStartsAt(event.target.value);
                      setConfirmed(false);
                    }}
                  />
                </Field>
                <p>{copy.appointmentLocalTime}</p>
                <label>
                  <input
                    type="checkbox"
                    required
                    checked={confirmed}
                    onChange={(event) => setConfirmed(event.target.checked)}
                  />
                  {copy.billingConfirmRenewal}
                </label>
                <button disabled={!plan || !confirmed || mutation.busy}>
                  {copy.billingRequestRenewal}
                </button>
              </form>
            </>
          )}
          {canWrite && largerPlans?.length === 0 && (
            <p>{copy.billingLargestPlan}</p>
          )}
          {canWrite && largerPlans && largerPlans.length > 0 && (
            <form
              className="billing-renewal plan-upgrade"
              onSubmit={(event) =>
                onSubmit(event, () =>
                  mutation.run(async () => {
                    if (!larger || !largerConfirmed) return;
                    await request(`${apiBase}billing/upgrade`, idSchema, {
                      csrf: session.csrf,
                      body: { id: generateUuidV7(), plan: larger },
                    });
                    overview.reload();
                    setLargerConfirmed(false);
                  })
                )
              }
            >
              <h3>{copy.billingUpgrade}</h3>
              <p>{copy.billingUpgradeHelp}</p>
              {overview.data.policy.autoRenew && (
                <p>{copy.billingUpgradeRenews}</p>
              )}
              <Field label={copy.billingUpgradePlan}>
                <select
                  required
                  value={larger ?? ''}
                  onChange={(event) => {
                    setLarger(planCodeSchema.parse(event.target.value));
                    setLargerConfirmed(false);
                  }}
                >
                  <option disabled value="">
                    {copy.choose}
                  </option>
                  {largerPlans.map((item) => (
                    <option key={item.code} value={item.code}>
                      {numberLabel(
                        String(item.includedConversations),
                        language
                      )}{' '}
                      {copy.billingPlanConversations} ·{' '}
                      {formatExactMoney(
                        item.monthlyPriceUsdMinor,
                        'USD',
                        2,
                        locale
                      )}{' '}
                      {copy.billingPerMonth}
                    </option>
                  ))}
                </select>
              </Field>
              <label>
                <input
                  type="checkbox"
                  required
                  checked={largerConfirmed}
                  onChange={(event) => setLargerConfirmed(event.target.checked)}
                />
                {copy.billingConfirmUpgrade}
              </label>
              <button disabled={!larger || !largerConfirmed || mutation.busy}>
                {copy.billingRequestUpgrade}
              </button>
            </form>
          )}
          {overview.data.periods.map((period) => (
            <article className="action-summary" key={period.id}>
              <strong>
                {period.plan} · {statusLabel(copy, period.state)}
              </strong>
              <p>
                {dateLabel(period.startsAt, language)} —{' '}
                {dateLabel(period.endsAt, language)}
              </p>
              <p>
                {formatExactMoney(
                  BigInt(period.subtotalMinor),
                  'USD',
                  2,
                  language === 'es' ? 'es-PE' : 'en-US'
                )}{' '}
                {copy.billingBeforeTax}
              </p>
              {period.invoiceReference && (
                <p>
                  {copy.billingInvoice}: {period.invoiceReference}
                </p>
              )}
              {period.paymentReference && (
                <p>
                  {copy.billingPayment}: {period.paymentReference}
                </p>
              )}
            </article>
          ))}
        </>
      )}
    </section>
  );
}
